import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Stack,
  Skeleton,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  AccountBalance as BalanceIcon,
  TrendingUp as ProfitIcon,
  TrendingDown as LossIcon,
  AttachMoney as MoneyIcon,
  AccountBalanceWallet as WalletIcon,
  ArrowUpward as IncomeIcon,
  ArrowDownward as ExpenseIcon,
  Savings as AssetIcon,
  CreditCard as LiabilityIcon,
  Savings as EquityIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  Today as TodayIcon,
  DateRange as WeekIcon,
  CalendarMonth as MonthIcon,
  Event as AllIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  LocalAtm as CashIcon,
  PointOfSale as PosIcon,
  ShoppingCart as PurchaseIcon,
  LocalShipping as SupplierIcon,
  Person as StaffIcon,
  Home as RentIcon,
  Bolt as UtilityIcon,
  Build as RepairIcon,
  MoreHoriz as OtherIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const EXPENSE_CATEGORIES = [
  { value: 'inventory_purchase', label: 'Inventory Purchase', labelUrdu: 'سامان خریداری', icon: <PurchaseIcon />, color: '#ff5722' },
  { value: 'salary', label: 'Staff Salary', labelUrdu: 'تنخواہ', icon: <StaffIcon />, color: '#2196f3' },
  { value: 'rent', label: 'Shop Rent', labelUrdu: 'کرایہ', icon: <RentIcon />, color: '#9c27b0' },
  { value: 'utilities', label: 'Utilities (Bill)', labelUrdu: 'بلز', icon: <UtilityIcon />, color: '#ff9800' },
  { value: 'maintenance', label: 'Maintenance', labelUrdu: 'مرمت', icon: <RepairIcon />, color: '#795548' },
  { value: 'supplier_payment', label: 'Supplier Payment', labelUrdu: 'سپلائر ادائیگی', icon: <SupplierIcon />, color: '#607d8b' },
  { value: 'marketing', label: 'Marketing', labelUrdu: 'مارکیٹنگ', icon: <OtherIcon />, color: '#e91e63' },
  { value: 'other', label: 'Other', labelUrdu: 'دیگر', icon: <OtherIcon />, color: '#757575' },
];

const PERIODS = [
  { value: 'today', label: 'Today', labelUrdu: 'آج', icon: <TodayIcon /> },
  { value: 'week', label: 'This Week', labelUrdu: 'اس ہفتے', icon: <WeekIcon /> },
  { value: 'month', label: 'This Month', labelUrdu: 'اس مہینے', icon: <MonthIcon /> },
  { value: 'quarter', label: 'This Quarter', labelUrdu: 'اس سہ ماہی', icon: <AllIcon /> },
  { value: 'year', label: 'This Year', labelUrdu: 'اس سال', icon: <CalendarIcon /> },
  { value: 'custom', label: 'Custom', labelUrdu: 'کسٹم', icon: <AllIcon /> },
];

const Accounts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState(0);
  const [period, setPeriod] = useState('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [loading, setLoading] = useState(false);

  // Data states
  const [expenses, setExpenses] = useState([]);
  const [orders, setOrders] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [settings, setSettings] = useState({});

  // Summary states
  const [summary, setSummary] = useState({
    totalSales: 0,
    totalExpenses: 0,
    grossProfit: 0,
    netProfit: 0,
    totalAssets: 0,
    totalLiabilities: 0,
    totalEquity: 0,
    cashInHand: 0,
    bankBalance: 0,
    receivables: 0,
    payables: 0,
    inventoryValue: 0,
  });

  // Dialogs
  const [expenseDialog, setExpenseDialog] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    category: 'inventory_purchase',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    receipt_number: '',
    notes: '',
  });

  const [detailDialog, setDetailDialog] = useState(false);
  const [detailTitle, setDetailTitle] = useState('');
  const [detailData, setDetailData] = useState([]);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ==================== HELPER: SAFE API ACCESS ====================
  const getAPI = useCallback(() => {
    if (typeof window !== 'undefined' && window.electronAPI) {
      return window.electronAPI;
    }
    return null;
  }, []);

  // ==================== HELPER: STORAGE ACCESS (Browser Mode) ====================
  const getStorage = useCallback(async () => {
    try {
      const module = await import('../database/storage');
      const storage = module.default || module;
      await storage.init();
      return storage;
    } catch (e) {
      console.warn('[Accounts] Storage not available:', e);
      return null;
    }
  }, []);

  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // ==================== DATE HELPERS ====================
  const getDateRange = useCallback((p) => {
    const now = new Date();
    const today = formatDate(now);
    
    switch (p) {
      case 'today': return { from: today, to: today };
      case 'week': {
        const start = new Date(now);
        start.setDate(now.getDate() - now.getDay() + 1);
        return { from: formatDate(start), to: today };
      }
      case 'month': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return { from: formatDate(start), to: today };
      }
      case 'quarter': {
        const quarter = Math.floor(now.getMonth() / 3);
        const start = new Date(now.getFullYear(), quarter * 3, 1);
        return { from: formatDate(start), to: today };
      }
      case 'year': {
        const start = new Date(now.getFullYear(), 0, 1);
        return { from: formatDate(start), to: today };
      }
      case 'custom': return { from: customFrom || today, to: customTo || today };
      default: return { from: today, to: today };
    }
  }, [customFrom, customTo]);

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDateDisplay = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatPrice = (amount) => `PKR ${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // ==================== CALCULATE SUMMARY ====================
  const calculateSummary = useCallback((expData, ordData, invData) => {
    const { from, to } = getDateRange(period);
    
    const filteredOrders = ordData.filter((o) => {
      const d = o.billed_at || o.created_at;
      return d >= from && d <= to && o.status === 'billed';
    });
    
    const filteredExpenses = expData.filter((e) => e.date >= from && e.date <= to);

    const totalSales = filteredOrders.reduce((sum, o) => sum + (o.final_amount || 0), 0);
    const totalExpenses = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
    const cogs = filteredExpenses.filter((e) => e.category === 'inventory_purchase').reduce((sum, e) => sum + e.amount, 0);
    
    const grossProfit = totalSales - cogs;
    const netProfit = grossProfit - (totalExpenses - cogs);
    
    const inventoryValue = invData.reduce((sum, i) => sum + (i.current_stock * i.cost_per_unit), 0);
    
    const cashSales = filteredOrders.filter((o) => o.payment_method === 'cash').reduce((sum, o) => sum + o.final_amount, 0);
    const cashExpenses = filteredExpenses.filter((e) => e.payment_method === 'cash').reduce((sum, e) => sum + e.amount, 0);
    const cashInHand = cashSales - cashExpenses;

    setSummary({
      totalSales,
      totalExpenses,
      grossProfit,
      netProfit,
      totalAssets: inventoryValue + cashInHand,
      totalLiabilities: filteredExpenses.filter((e) => e.category === 'supplier_payment').reduce((sum, e) => sum + e.amount, 0),
      totalEquity: netProfit,
      cashInHand,
      bankBalance: filteredOrders.filter((o) => o.payment_method === 'card' || o.payment_method === 'online').reduce((sum, o) => sum + o.final_amount, 0) - filteredExpenses.filter((e) => e.payment_method !== 'cash').reduce((sum, e) => sum + e.amount, 0),
      receivables: 0,
      payables: filteredExpenses.filter((e) => e.category === 'supplier_payment').reduce((sum, e) => sum + e.amount, 0),
      inventoryValue,
    });
  }, [period, getDateRange]);

  // ==================== LOAD DATA ====================
  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = getDateRange(period);
      
      const api = getAPI();
      
      // ✅ BROWSER MODE - Use storage.js
      if (!api) {
        console.log('[Accounts] Browser mode - using storage');
        const storage = await getStorage();
        
        if (!storage) {
          throw new Error('Storage not available');
        }
        
        const [expData, ordData, invData, settData] = await Promise.all([
          storage.getExpenses({ fromDate: from, toDate: to }),
          storage.getOrders({}),
          storage.getInventory(),
          storage.getSettings(),
        ]);
        
        setExpenses(expData || []);
        setOrders((ordData || []).filter((o) => o.status === 'billed'));
        setInventory(invData || []);
        setSettings(settData || {});
        
        calculateSummary(expData || [], ordData || [], invData || []);
        setLoading(false);
        return;
      }
      
      // ✅ ELECTRON MODE
      console.log('[Accounts] Electron mode - using electronAPI');
      const [expData, ordData, invData, settData] = await Promise.all([
        api.getExpenses({ fromDate: from, toDate: to }),
        api.getOrders({}),
        api.getInventory(),
        api.getSettings(),
      ]);

      setExpenses(expData || []);
      setOrders((ordData || []).filter((o) => o.status === 'billed'));
      setInventory(invData || []);
      setSettings(settData || {});

      calculateSummary(expData || [], ordData || [], invData || []);
    } catch (err) {
      console.error('[Accounts] Load error:', err);
      showMessage('❌ Data load nahi ho saki!', 'error');
    } finally {
      setLoading(false);
    }
  }, [period, customFrom, customTo, getDateRange, getAPI, getStorage, calculateSummary, showMessage]);

  useEffect(() => {
    loadAllData();
  }, [period, customFrom, customTo, loadAllData]);

  // ==================== SAVE EXPENSE ====================
  const saveExpense = async () => {
    if (!expenseForm.amount || !expenseForm.description) {
      showMessage('❌ Amount and description required!', 'error');
      return;
    }
    try {
      const api = getAPI();
      const expenseData = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
      };
      
      if (!api) {
        // ✅ BROWSER MODE
        console.log('[Accounts] Browser mode - saving expense via storage');
        const storage = await getStorage();
        if (storage) {
          await storage.addExpense(expenseData);
        } else {
          throw new Error('Storage not available');
        }
      } else {
        // ✅ ELECTRON MODE
        await api.addExpense(expenseData);
      }
      
      setExpenseDialog(false);
      setExpenseForm({
        category: 'inventory_purchase',
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        receipt_number: '',
        notes: '',
      });
      showMessage('✅ Expense added successfully!', 'success');
      loadAllData();
    } catch (err) {
      console.error('[Accounts] Expense save error:', err);
      showMessage('❌ Expense save nahi ho saka!', 'error');
    }
  };

  // ==================== RENDER FUNCTIONS ====================
  const StatCard = ({ title, titleUrdu, value, icon, color, bg }) => (
    <Grid item xs={6} sm={6} md={3}>
      <Fade in timeout={300}>
        <Card sx={{ 
          borderRadius: 3, 
          borderLeft: `4px solid ${color}`, 
          bgcolor: bg, 
          height: '100%',
          transition: 'all 0.3s',
          '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' },
        }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Avatar sx={{ bgcolor: color, color: '#fff', width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 }, mb: 1 }}>
              {icon}
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 700, color: color, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              {value}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem', display: { xs: 'block', sm: 'block' } }}>
              {titleUrdu}
            </Typography>
          </CardContent>
        </Card>
      </Fade>
    </Grid>
  );

  // ===== OVERVIEW =====
  const renderOverview = () => (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <StatCard title="Total Sales" titleUrdu="کل فروخت" value={formatPrice(summary.totalSales)} icon={<MoneyIcon />} color="#2e7d32" bg="#e8f5e9" />
        <StatCard title="Total Expenses" titleUrdu="کل اخراجات" value={formatPrice(summary.totalExpenses)} icon={<ExpenseIcon />} color="#d32f2f" bg="#ffebee" />
        <StatCard title="Gross Profit" titleUrdu="مجموعی منافع" value={formatPrice(summary.grossProfit)} icon={<ProfitIcon />} color="#1976d2" bg="#e3f2fd" />
        <StatCard title="Net Profit" titleUrdu="خالص منافع" value={formatPrice(summary.netProfit)} icon={summary.netProfit >= 0 ? <ProfitIcon /> : <LossIcon />} color={summary.netProfit >= 0 ? '#4caf50' : '#f44336'} bg={summary.netProfit >= 0 ? '#e8f5e9' : '#ffebee'} />
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, bgcolor: '#1a1a2e', color: '#fff' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#ff6b35', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Financial Position / مالی حیثیت
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Total Assets / کل اثاثے</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#4caf50' }}>{formatPrice(summary.totalAssets)}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={100} sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#4caf50' } }} />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>Total Liabilities / کل ذمہ داریاں</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#f44336' }}>{formatPrice(summary.totalLiabilities)}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={summary.totalAssets > 0 ? (summary.totalLiabilities / summary.totalAssets) * 100 : 0} sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#f44336' } }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>Owner's Equity / مالکانہ حقوق</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#ff6b35' }}>{formatPrice(summary.totalEquity)}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={summary.totalAssets > 0 ? (summary.totalEquity / summary.totalAssets) * 100 : 0} sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#ff6b35' } }} />
            </Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Accounting Equation</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: Math.abs(summary.totalAssets - (summary.totalLiabilities + summary.totalEquity)) < 1 ? '#4caf50' : '#f44336', fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                {formatPrice(summary.totalAssets)} = {formatPrice(summary.totalLiabilities)} + {formatPrice(summary.totalEquity)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Cash Position / نقدی کی حیثیت
            </Typography>
            {[
              { label: 'Cash in Hand', labelUrdu: 'ہاتھ میں نقدی', value: summary.cashInHand, color: '#4caf50', icon: <CashIcon /> },
              { label: 'Bank Balance', labelUrdu: 'بینک بیلنس', value: summary.bankBalance, color: '#2196f3', icon: <BalanceIcon /> },
              { label: 'Inventory Value', labelUrdu: 'اسٹاک کی قیمت', value: summary.inventoryValue, color: '#ff9800', icon: <AssetIcon /> },
              { label: 'Payables', labelUrdu: 'ادائیگیاں', value: summary.payables, color: '#f44336', icon: <LiabilityIcon /> },
            ].map((item, idx) => (
              <Box key={idx} sx={{ mb: { xs: 1, sm: 2 }, p: { xs: 1, sm: 1.5 }, borderRadius: 2, bgcolor: `${item.color}08`, border: `1px solid ${item.color}20` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                    <Avatar sx={{ bgcolor: `${item.color}15`, color: item.color, width: { xs: 30, sm: 36 }, height: { xs: 30, sm: 36 } }}>
                      {item.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a2e', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {item.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
                        {item.labelUrdu}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: item.color, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                    {formatPrice(item.value)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  // ===== PROFIT & LOSS =====
  const renderProfitLoss = () => {
    const { from, to } = getDateRange(period);
    const filteredOrders = orders.filter((o) => {
      const d = o.billed_at || o.created_at;
      return d >= from && d <= to;
    });
    const filteredExpenses = expenses.filter((e) => e.date >= from && e.date <= to);

    const totalRevenue = filteredOrders.reduce((sum, o) => sum + (o.final_amount || 0), 0);
    const cogs = filteredExpenses.filter((e) => e.category === 'inventory_purchase').reduce((sum, e) => sum + e.amount, 0);
    const grossProfit = totalRevenue - cogs;
    
    const operatingExpenses = filteredExpenses.filter((e) => e.category !== 'inventory_purchase');
    const totalOperatingExp = operatingExpenses.reduce((sum, e) => sum + e.amount, 0);
    const netProfit = grossProfit - totalOperatingExp;

    const expenseByCategory = {};
    operatingExpenses.forEach((e) => {
      if (!expenseByCategory[e.category]) expenseByCategory[e.category] = 0;
      expenseByCategory[e.category] += e.amount;
    });

    return (
      <Box>
        <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', textAlign: 'center', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
            Profit & Loss Statement / منافع و نقصان کا بیان
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center', mb: 3 }}>
            Period: {from} to {to}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32', mb: 2, pb: 1, borderBottom: '2px solid #e8f5e9', fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              REVENUE / آمدنی
            </Typography>
            <Box sx={{ pl: { xs: 1, sm: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>Sales Revenue</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatPrice(totalRevenue)}</Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Net Revenue</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2e7d32' }}>{formatPrice(totalRevenue)}</Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#d32f2f', mb: 2, pb: 1, borderBottom: '2px solid #ffebee', fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              COST OF GOODS SOLD / فروخت شدہ سامان کی قیمت
            </Typography>
            <Box sx={{ pl: { xs: 1, sm: 3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>Inventory Purchases</Typography>
                <Typography variant="body1">{formatPrice(cogs)}</Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total COGS</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#d32f2f' }}>{formatPrice(cogs)}</Typography>
            </Box>
          </Box>

          <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>GROSS PROFIT / مجموعی منافع</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2', fontSize: { xs: '1rem', sm: '1.5rem' } }}>{formatPrice(grossProfit)}</Typography>
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Gross Margin: {totalRevenue > 0 ? ((grossProfit / totalRevenue) * 100).toFixed(1) : 0}%
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800', mb: 2, pb: 1, borderBottom: '2px solid #fff3e0', fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              OPERATING EXPENSES / آپریٹنگ اخراجات
            </Typography>
            <Box sx={{ pl: { xs: 1, sm: 3 } }}>
              {Object.entries(expenseByCategory).map(([cat, amount]) => {
                const catInfo = EXPENSE_CATEGORIES.find((c) => c.value === cat) || EXPENSE_CATEGORIES[7];
                return (
                  <Box key={cat} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                      {catInfo.icon} {catInfo.label}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>{formatPrice(amount)}</Typography>
                  </Box>
                );
              })}
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total Operating Expenses</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#ff9800' }}>{formatPrice(totalOperatingExp)}</Typography>
            </Box>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: netProfit >= 0 ? '#e8f5e9' : '#ffebee', borderRadius: 3, border: `2px solid ${netProfit >= 0 ? '#4caf50' : '#f44336'}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: netProfit >= 0 ? '#2e7d32' : '#d32f2f', fontSize: { xs: '1rem', sm: '1.5rem' } }}>
                  NET {netProfit >= 0 ? 'PROFIT' : 'LOSS'} / خالص {netProfit >= 0 ? 'منافع' : 'نقصان'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Net Margin: {totalRevenue > 0 ? ((Math.abs(netProfit) / totalRevenue) * 100).toFixed(1) : 0}%
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: netProfit >= 0 ? '#2e7d32' : '#d32f2f', fontSize: { xs: '1.2rem', sm: '2rem' } }}>
                {netProfit >= 0 ? '' : '-'} {formatPrice(Math.abs(netProfit))}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  };

  // ===== BALANCE SHEET =====
  const renderBalanceSheet = () => (
    <Box>
      <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', textAlign: 'center', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
          Balance Sheet / بیلنس شیٹ
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center', mb: 3 }}>
          As of {new Date().toLocaleDateString('en-GB')}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, bgcolor: '#e8f5e9', border: '2px solid #4caf50' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32', mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                <AssetIcon /> ASSETS / اثاثے
              </Typography>
              
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a1a2e', mb: 1, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>Current Assets / رواں اثاثے</Typography>
              <Box sx={{ pl: { xs: 1, sm: 2 }, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Cash in Hand / ہاتھ میں نقدی</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatPrice(summary.cashInHand)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Bank Balance / بینک بیلنس</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatPrice(summary.bankBalance)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Inventory / اسٹاک</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatPrice(summary.inventoryValue)}</Typography>
                </Box>
              </Box>
              <Divider sx={{ borderColor: '#4caf5030' }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, p: 1, bgcolor: '#fff', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2e7d32' }}>Total Assets</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2e7d32' }}>{formatPrice(summary.totalAssets)}</Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, bgcolor: '#ffebee', border: '2px solid #f44336', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#d32f2f', mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                <LiabilityIcon /> LIABILITIES / ذمہ داریاں
              </Typography>
              
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a1a2e', mb: 1, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>Current Liabilities / رواں ذمہ داریاں</Typography>
              <Box sx={{ pl: { xs: 1, sm: 2 }, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Supplier Payables / سپلائرز</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatPrice(summary.payables)}</Typography>
                </Box>
              </Box>
              <Divider sx={{ borderColor: '#f4433630' }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, p: 1, bgcolor: '#fff', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#d32f2f' }}>Total Liabilities</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#d32f2f' }}>{formatPrice(summary.totalLiabilities)}</Typography>
              </Box>
            </Paper>

            <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, bgcolor: '#e3f2fd', border: '2px solid #2196f3' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                <EquityIcon /> OWNER'S EQUITY / مالکانہ حقوق
              </Typography>
              
              <Box sx={{ pl: { xs: 1, sm: 2 }, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Retained Earnings / محفوظ آمدنی</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatPrice(summary.totalEquity)}</Typography>
                </Box>
              </Box>
              <Divider sx={{ borderColor: '#2196f330' }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, p: 1, bgcolor: '#fff', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1976d2' }}>Total Equity</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1976d2' }}>{formatPrice(summary.totalEquity)}</Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ mt: 2, p: { xs: 2, sm: 2.5 }, borderRadius: 3, bgcolor: '#1a1a2e', color: '#fff', textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
            Accounting Equation / اکاؤنٹنگ مساوات
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#4caf50', fontSize: { xs: '1rem', sm: '1.5rem' } }}>{formatPrice(summary.totalAssets)}</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Assets</Typography>
            </Box>
            <Typography variant="h4" sx={{ color: '#fff', fontSize: { xs: '1.2rem', sm: '2rem' } }}>=</Typography>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#f44336', fontSize: { xs: '1rem', sm: '1.5rem' } }}>{formatPrice(summary.totalLiabilities)}</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Liabilities</Typography>
            </Box>
            <Typography variant="h4" sx={{ color: '#fff', fontSize: { xs: '1.2rem', sm: '2rem' } }}>+</Typography>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#2196f3', fontSize: { xs: '1rem', sm: '1.5rem' } }}>{formatPrice(summary.totalEquity)}</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Equity</Typography>
            </Box>
          </Box>
          <Chip 
            icon={Math.abs(summary.totalAssets - (summary.totalLiabilities + summary.totalEquity)) < 1 ? <CheckIcon /> : <WarningIcon />} 
            label={Math.abs(summary.totalAssets - (summary.totalLiabilities + summary.totalEquity)) < 1 ? "BALANCED / متوازن" : "NOT BALANCED / غیر متوازن"} 
            color={Math.abs(summary.totalAssets - (summary.totalLiabilities + summary.totalEquity)) < 1 ? "success" : "error"} 
            sx={{ fontWeight: 700 }} 
          />
        </Paper>
      </Paper>
    </Box>
  );

  // ===== LEDGER =====
  const renderLedger = () => {
    const { from, to } = getDateRange(period);
    const filteredExpenses = expenses.filter((e) => e.date >= from && e.date <= to);

    return (
      <Box>
        <Box sx={{ mb: { xs: 2, sm: 3 }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Expense Ledger / اخراجات کی کتاب
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setExpenseDialog(true)}
            sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' }, borderRadius: 2, width: { xs: '100%', sm: 'auto' } }}
          >
            Add Expense
          </Button>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {EXPENSE_CATEGORIES.map((cat) => {
            const amount = filteredExpenses.filter((e) => e.category === cat.value).reduce((sum, e) => sum + e.amount, 0);
            if (amount === 0) return null;
            return (
              <Grid item xs={6} sm={4} md={3} key={cat.value}>
                <Card sx={{ borderRadius: 3, borderLeft: `4px solid ${cat.color}` }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Avatar sx={{ bgcolor: `${cat.color}15`, color: cat.color, width: { xs: 28, sm: 36 }, height: { xs: 28, sm: 36 } }}>{cat.icon}</Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>{cat.label}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.6rem', sm: '0.7rem' }, display: { xs: 'none', sm: 'block' } }}>{cat.labelUrdu}</Typography>
                      </Box>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: cat.color, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>{formatPrice(amount)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Category</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Description</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Receipt #</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>Payment</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' } }} align="right">Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, display: { xs: 'none', lg: 'table-cell' } }}>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredExpenses.length > 0 ? filteredExpenses.sort((a, b) => b.date.localeCompare(a.date)).map((expense, idx) => {
                  const catInfo = EXPENSE_CATEGORIES.find((c) => c.value === expense.category) || EXPENSE_CATEGORIES[7];
                  return (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{formatDateDisplay(expense.date)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={catInfo.icon}
                          label={catInfo.label}
                          sx={{ bgcolor: `${catInfo.color}15`, color: catInfo.color, fontWeight: 600, fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{expense.description}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>{expense.receipt_number || '-'}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip size="small" label={expense.payment_method?.toUpperCase()} variant="outlined" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{formatPrice(expense.amount)}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{expense.notes || '-'}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                }) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>No expenses found for this period</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };

  // ===== TRIAL BALANCE =====
  const renderTrialBalance = () => {
    const { from, to } = getDateRange(period);
    const filteredOrders = orders.filter((o) => {
      const d = o.billed_at || o.created_at;
      return d >= from && d <= to;
    });
    const filteredExpenses = expenses.filter((e) => e.date >= from && e.date <= to);

    const accounts = [
      { name: 'Cash in Hand', nameUrdu: 'ہاتھ میں نقدی', debit: summary.cashInHand > 0 ? summary.cashInHand : 0, credit: summary.cashInHand < 0 ? Math.abs(summary.cashInHand) : 0 },
      { name: 'Bank Account', nameUrdu: 'بینک اکاؤنٹ', debit: summary.bankBalance > 0 ? summary.bankBalance : 0, credit: summary.bankBalance < 0 ? Math.abs(summary.bankBalance) : 0 },
      { name: 'Inventory', nameUrdu: 'اسٹاک', debit: summary.inventoryValue, credit: 0 },
      { name: 'Sales Revenue', nameUrdu: 'فروخت کی آمدنی', debit: 0, credit: filteredOrders.reduce((sum, o) => sum + (o.final_amount || 0), 0) },
      { name: 'Cost of Goods Sold', nameUrdu: 'فروخت شدہ سامان کی قیمت', debit: filteredExpenses.filter((e) => e.category === 'inventory_purchase').reduce((sum, e) => sum + e.amount, 0), credit: 0 },
      ...EXPENSE_CATEGORIES.filter((cat) => cat.value !== 'inventory_purchase').map((cat) => ({
        name: cat.label,
        nameUrdu: cat.labelUrdu,
        debit: filteredExpenses.filter((e) => e.category === cat.value).reduce((sum, e) => sum + e.amount, 0),
        credit: 0,
      })),
      { name: 'Payables', nameUrdu: 'ادائیگیاں', debit: 0, credit: summary.payables },
    ].filter((acc) => acc.debit > 0 || acc.credit > 0);

    const totalDebit = accounts.reduce((sum, a) => sum + a.debit, 0);
    const totalCredit = accounts.reduce((sum, a) => sum + a.credit, 0);

    return (
      <Box>
        <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e', textAlign: 'center', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
            Trial Balance / ٹرائل بیلنس
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center', mb: 3 }}>
            For the period: {from} to {to}
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>#</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Account Name / اکاؤنٹ</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' } }} align="right">Debit / واجب الادا</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' } }} align="right">Credit / جمع</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((acc, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{idx + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{acc.name}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>{acc.nameUrdu}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ color: acc.debit > 0 ? '#1a1a2e' : 'text.disabled', fontWeight: acc.debit > 0 ? 600 : 400, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                      {acc.debit > 0 ? formatPrice(acc.debit) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: acc.credit > 0 ? '#1a1a2e' : 'text.disabled', fontWeight: acc.credit > 0 ? 600 : 400, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                      {acc.credit > 0 ? formatPrice(acc.credit) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>TOTAL / کل</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{formatPrice(totalDebit)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{formatPrice(totalCredit)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: Math.abs(totalDebit - totalCredit) < 1 ? '#e8f5e9' : '#ffebee', border: `2px solid ${Math.abs(totalDebit - totalCredit) < 1 ? '#4caf50' : '#f44336'}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: Math.abs(totalDebit - totalCredit) < 1 ? '#2e7d32' : '#d32f2f', fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                {Math.abs(totalDebit - totalCredit) < 1 ? '✓ BALANCED / متوازن' : '✗ NOT BALANCED / غیر متوازن'}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                Difference: {formatPrice(Math.abs(totalDebit - totalCredit))}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  };

  // ==================== MAIN RETURN ====================
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ mb: { xs: 2, sm: 3 }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' } }}>
            Accounts & Finance
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            اکاؤنٹس | Financial Management & Reporting
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ borderRadius: 2, fontSize: { xs: '0.7rem', sm: '0.875rem' }, px: { xs: 1.5, sm: 2 } }} size={isMobile ? 'small' : 'medium'}>
            Export
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon />} sx={{ borderRadius: 2, fontSize: { xs: '0.7rem', sm: '0.875rem' }, px: { xs: 1.5, sm: 2 } }} size={isMobile ? 'small' : 'medium'}>
            Print
          </Button>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={loadAllData} sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' }, borderRadius: 2, fontSize: { xs: '0.7rem', sm: '0.875rem' }, px: { xs: 1.5, sm: 2 } }} size={isMobile ? 'small' : 'medium'}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Period Filter */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 3, display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          Period / مدت:
        </Typography>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(e, val) => val && setPeriod(val)}
          size={isMobile ? 'small' : 'medium'}
          sx={{
            '& .MuiToggleButton-root': {
              borderRadius: '20px !important',
              px: { xs: 1, sm: 2 },
              py: { xs: 0.3, sm: 0.5 },
              textTransform: 'none',
              mx: 0.3,
              fontSize: { xs: '0.65rem', sm: '0.875rem' },
            },
            '& .Mui-selected': {
              bgcolor: '#1a1a2e !important',
              color: '#fff !important',
            },
          }}
        >
          {PERIODS.map((p) => (
            <ToggleButton key={p.value} value={p.value}>
              {isMobile ? p.icon : p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {period === 'custom' && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField type="date" size="small" label="From" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: 'auto' } }} />
            <TextField type="date" size="small" label="To" value={customTo} onChange={(e) => setCustomTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: 'auto' } }} />
          </Box>
        )}
      </Paper>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 3, mb: 2, overflow: 'auto' }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, val) => setActiveTab(val)} 
          sx={{ px: { xs: 1, sm: 2 }, pt: 1 }} 
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
        >
          <Tab icon={<BalanceIcon />} label={isMobile ? '' : "Overview"} sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }} />
          <Tab icon={<ProfitIcon />} label={isMobile ? '' : "P&L"} sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }} />
          <Tab icon={<BalanceIcon />} label={isMobile ? '' : "Balance Sheet"} sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }} />
          <Tab icon={<ReceiptIcon />} label={isMobile ? '' : "Expenses"} sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }} />
          <Tab icon={<CheckIcon />} label={isMobile ? '' : "Trial Balance"} sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }} />
        </Tabs>
      </Paper>

      {/* Content */}
      {loading ? (
        <Box sx={{ py: 4 }}>
          <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 3, mb: 2 }} />
          <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 3 }} />
        </Box>
      ) : (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          {activeTab === 0 && renderOverview()}
          {activeTab === 1 && renderProfitLoss()}
          {activeTab === 2 && renderBalanceSheet()}
          {activeTab === 3 && renderLedger()}
          {activeTab === 4 && renderTrialBalance()}
        </Box>
      )}

      {/* Expense Dialog */}
      <Dialog 
        open={expenseDialog} 
        onClose={() => setExpenseDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1a1a2e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Add Expense / اخراجات شامل کریں
          <IconButton onClick={() => setExpenseDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Category / کیٹیگری</InputLabel>
                <Select
                  value={expenseForm.category}
                  label="Category"
                  onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}
                >
                  {EXPENSE_CATEGORIES.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {cat.icon}
                        <Box>
                          <Typography variant="body2">{cat.label}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{cat.labelUrdu}</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description / تفصیل"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                placeholder="e.g., Monthly rent, Electricity bill"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Amount / رقم"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">PKR</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date / تاریخ"
                value={expenseForm.date}
                onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={expenseForm.payment_method}
                  label="Payment Method"
                  onChange={(e) => setExpenseForm({ ...expenseForm, payment_method: e.target.value })}
                >
                  <MenuItem value="cash">Cash / نقد</MenuItem>
                  <MenuItem value="card">Card / کارڈ</MenuItem>
                  <MenuItem value="online">Online / آن لائن</MenuItem>
                  <MenuItem value="cheque">Cheque / چیک</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Receipt # / رسید نمبر"
                value={expenseForm.receipt_number}
                onChange={(e) => setExpenseForm({ ...expenseForm, receipt_number: e.target.value })}
                placeholder="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes / نوٹس"
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                placeholder="Any additional information..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setExpenseDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={saveExpense}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!expenseForm.amount || !expenseForm.description}
            sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' }, borderRadius: 2 }}
          >
            Save Expense
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Accounts;